import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { AlertColor, IconButton, InputAdornment, Link, Stack } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CheckRounded, ClearRounded, Visibility, VisibilityOff } from '@mui/icons-material'
import { authAPI } from '../../shared/api/AuthAPI'
import { useAppSelector } from '../../store/hooks'
import { ReactComponent as WelcomeLogo } from '../../assets/Welcome.svg'
import { ReactComponent as ErrorLogo } from '../../assets/error_404.svg'
import { ReactComponent as CongratsLogo } from '../../assets/Congrats.svg'
import FsAuthWrapper from './auth-wrapper'
import LoadingDash from '../common/atoms/LoadingDash'

const FsResetPassword = () => {
  const navigate = useNavigate()
  const { currentLanguage } = useAppSelector((state) => state.currentUser)
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [severity, setSeverity] = useState<AlertColor>('error')
  const [showWelcome, setShowWelcome] = useState<boolean>(false)
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [newPassword, setNewPassword] = useState<string>('')
  const [newPasswordConf, setNewPasswordConf] = useState<string>('')
  const [showPasswordConf, setShowPasswordConf] = useState<boolean>(false)
  const [pwdErrors, setPwdErrors] = useState<boolean[]>([false, false, false, false, false, false])
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()

  const isReset = searchParams.get('reset')

  useEffect(() => {
    if (searchParams.get('reset') !== 'true') setShowWelcome(true)
  }, [searchParams])

  const handlePasswordReset = async () => {
    try {
      setLoading(true)
      const email = searchParams.get('email')
      const token = searchParams.get('token')

      if (!email || !token) {
        setSeverity('error')
        setMessage('invalid.token')
        return
      }

      await authAPI.resetPassword(email.toLowerCase(), newPassword, token)
      setSeverity('success')
      setMessage('reset.success')
    } catch (e: any) {
      setSeverity('error')
      if (e.response.status === 400) {
        setMessage('invalid.token')
      } else if (e.response.status === 404) {
        setMessage('invalid.email')
      } else {
        setMessage('something.went.wrong')
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const pwdErr = [false, false, false, false, false, false]
    if (newPassword.length < 8) pwdErr[0] = true
    if (newPassword.match(/[a-z]+/) === null) pwdErr[1] = true
    if (newPassword.match(/[A-Z]+/) === null) pwdErr[2] = true
    if (newPassword.match(/[@$!%*#?&]+/) === null) pwdErr[3] = true
    if (newPassword.match(/\d+/) === null) pwdErr[4] = true
    if (newPassword !== newPasswordConf) pwdErr[5] = true
    setPwdErrors(pwdErr)
  }, [newPassword, newPasswordConf])

  const PwdErrRule = (errNum: number) => (
    <Stack direction="row" alignItems="center">
      {pwdErrors[errNum] ? (
        <ClearRounded fontSize="small" color="error" />
      ) : (
        <CheckRounded fontSize="small" color="success" />
      )}
      <Typography ml={1} color={pwdErrors[errNum] ? 'error' : 'success.main'} variant="body2">
        {t(`password.err.${errNum}` as any)}
      </Typography>
    </Stack>
  )

  useEffect(() => {
    const listener = (event: any) => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        event.preventDefault()
        if (!loading && newPassword && newPasswordConf && !pwdErrors.find((err) => err))
          handlePasswordReset()
      }
    }
    document.addEventListener('keydown', listener)
    return () => {
      document.removeEventListener('keydown', listener)
    }
  }, [newPassword, newPasswordConf, pwdErrors])

  return (
    <FsAuthWrapper>
      <Box
        sx={{
          marginTop: 3.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {showWelcome ? (
          <>
            <Typography variant="h2" color="primary">
              {t('welcome.title')}
            </Typography>
            <Typography textAlign="center" mt={2} mb={4}>
              {t('welcome.desc')}
            </Typography>
            <WelcomeLogo />
            <Typography textAlign="center" mt={4}>
              {t('welcome.instructions')}
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 3, mb: 2, width: 150 }}
              onClick={() => setShowWelcome(false)}
            >
              {t('welcome.start')}
            </Button>
          </>
        ) : message ? (
          <>
            <Typography variant="h2" color="primary" mb={4} textAlign="center">
              {t(severity === 'success' ? 'walkthrough.36' : 'something.went.wrong.small')}
            </Typography>
            {severity === 'success' ? <CongratsLogo /> : <ErrorLogo />}
            {severity === 'success' ? (
              <Typography mt={4} textAlign="center">
                {t(message as any)}
              </Typography>
            ) : (
              <Typography color="error" textAlign="center" mt={1} mb={2}>
                {`${t(message as any)} `}
                <Link href="mailto:support@ines-analytics.com" color="error">
                  {currentLanguage === 'de' ? 'kontaktieren Sie uns' : 'contact us'}
                </Link>
              </Typography>
            )}

            <Button
              variant="contained"
              sx={{ mt: 3, mb: 2, width: 250 }}
              onClick={() =>
                severity === 'success' ? navigate('/login') : navigate('/forgot-password')
              }
            >
              {severity === 'success' ? `${t('go.to.login')}` : `${t('generate.link')}`}
            </Button>
          </>
        ) : (
          <>
            <Typography variant="h1" color="primary">
              {t(isReset ? 'reset.password' : 'set.new.password')}
            </Typography>
            <Box sx={{ mt: 1, width: 350, maxWidth: '100%' }}>
              <TextField
                margin="normal"
                required
                size="small"
                fullWidth
                autoFocus
                name="password"
                label={t('password')}
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box>
                {newPassword && (
                  <>
                    {PwdErrRule(0)}
                    {PwdErrRule(1)}
                    {PwdErrRule(2)}
                    {PwdErrRule(3)}
                    {PwdErrRule(4)}
                  </>
                )}
                <TextField
                  margin="normal"
                  size="small"
                  required
                  fullWidth
                  name="password-conf"
                  label={t('password.confirm')}
                  type={showPasswordConf ? 'text' : 'password'}
                  id="password-conf"
                  autoComplete="current-password-conf"
                  value={newPasswordConf}
                  onChange={(event) => setNewPasswordConf(event.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPasswordConf(!showPasswordConf)}
                          edge="end"
                        >
                          {showPasswordConf ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {newPasswordConf ? (
                  pwdErrors[5] ? (
                    PwdErrRule(5)
                  ) : (
                    <Stack direction="row" alignItems="center">
                      <CheckRounded fontSize="small" color="success" />
                      <Typography ml={1} color="success.main" variant="body2">
                        {`${t('password.err.match')}`}
                      </Typography>
                    </Stack>
                  )
                ) : null}
              </Box>
              <Button
                type="submit"
                disabled={
                  loading || !newPassword || !newPasswordConf || pwdErrors.find((err) => err)
                }
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                onClick={handlePasswordReset}
              >
                {t('password.set')}
              </Button>
            </Box>
          </>
        )}
      </Box>
      {loading && (
        <Box sx={{ position: 'absolute' }}>
          <LoadingDash title={t('loading')} />
        </Box>
      )}
    </FsAuthWrapper>
  )
}

export default FsResetPassword
